import { memo, useCallback, useMemo, useState } from 'react'
import { TableColumnsType, Table, Space, Modal } from 'antd'
import { TeamToLeague } from 'types/leagues'
import { PlayersToTeams } from '../players-to-teams'
import { modalTitle } from 'utils/modal-title'

type Props = {
    data: TeamToLeague[]
    leagueName: string
    onDeleteTeam: (teamId: number, leagueId: number) => void
}

export const ExpandableRow = memo((props: Props) => {
    const { data, leagueName, onDeleteTeam } = props
    const [selectedTeamInLeague, setSelectedTeamInLeague] = useState<{
        teamId: number | null
        leagueId: number | null
        teamName: string | null
    }>({
        teamId: null,
        leagueId: null,
        teamName: null,
    })
    const [open, setOpen] = useState<boolean>(false)

    const toggleModal = useCallback(() => {
        setOpen(open => !open)
        if (open) {
            setSelectedTeamInLeague({
                teamId: null,
                leagueId: null,
                teamName: null,
            })
        }
    }, [open])

    const handleShowPlayers = useCallback(
        (teamId: number, leagueId: number, teamName: string) => {
            setSelectedTeamInLeague({
                teamId,
                leagueId,
                teamName,
            })
            toggleModal()
        },
        [toggleModal]
    )

    const playersModalTitle = useMemo(() => {
        if (!selectedTeamInLeague.teamName) {
            return 'Players to team'
        }

        return modalTitle('Players to team', `${selectedTeamInLeague.teamName} (${leagueName})`)
    }, [leagueName, selectedTeamInLeague.teamName])

    const columns: TableColumnsType<TeamToLeague> = [
        { title: 'Team ID', dataIndex: ['team', 'id'] },
        { title: 'Team Name', dataIndex: ['team', 'name'] },
        { title: 'Team Short name', dataIndex: ['team', 'short_name'] },
        {
            title: 'Action',
            key: 'operation',
            render: (_, record) => (
                <Space size='middle'>
                    <a onClick={() => onDeleteTeam(record.teamId, record.leagueId)}>Delete</a>
                    <a onClick={() => handleShowPlayers(record.teamId, record.leagueId, record.team.name)}>Players</a>
                </Space>
            ),
        },
    ]

    return (
        <>
            <Table columns={columns} dataSource={data} pagination={false} rowKey={'id'} />
            <Modal
                title={playersModalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <PlayersToTeams
                    teamId={selectedTeamInLeague.teamId!}
                    leagueId={selectedTeamInLeague.leagueId!}
                    teamName={selectedTeamInLeague.teamName ?? undefined}
                />
            </Modal>
        </>
    )
})
